import InputRange from 'react-input-range'
import 'react-input-range/lib/css/index.css'
import React, { useState, useEffect, useRef, useCallback, memo } from 'react'
import CustomField from '../../../form/CustomField'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import { useSearchContext } from '/providers/SearchContext'
import styles from './styles.module.scss'
import classNames from 'classnames'

const Price = memo(
  ({
    form: { setFieldValue },
    setPriceActive,
    shadowRef,
    price,
    translation,
    ...props
  }) => {
    const [value, setValue] = useState({ min: 0, max: price })
    const { search } = useSearchContext()
    const [show, setShow] = useState(false)
    const wrapperRef = useRef(null)
    const { isTablet } = useResponsive()

    // Memoize click outside handler
    const handleClickOutside = useCallback((event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false)
      }
    }, [])

    // Set up click outside handler
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [handleClickOutside])

    // Memoize input range change handler
    const handleChange = useCallback(
      (newValue) => {
        // Use precision formatting for numeric values
        const formattedValue = {
          min: Number(newValue.min.toFixed(2)),
          max: Number(newValue.max.toFixed(2))
        }

        setValue(formattedValue)
        setFieldValue('priceStart', formattedValue.min)
        setFieldValue('priceEnd', formattedValue.max)
      },
      [setFieldValue]
    )

    // Sync with search context
    useEffect(() => {
      if (search.priceStart !== null || search.priceEnd !== null) {
        setValue({
          min: search.priceStart ?? 0,
          max: search.priceEnd ?? price
        })
      }
    }, [search.priceEnd, search.priceStart, price])

    // Update parent component's active state
    useEffect(() => {
      if (setPriceActive) {
        setPriceActive(value.max !== price || value.min !== 0)
      }
    }, [value, price, setPriceActive])

    // Update form values when local state changes
    useEffect(() => {
      setFieldValue('priceStart', value.min)
      setFieldValue('priceEnd', value.max)
    }, [value, setFieldValue])

    // Handle shadow overlay
    useEffect(() => {
      if (shadowRef) {
        if (show) {
          shadowRef.current?.classList.add('isVisible')
        } else {
          shadowRef.current?.classList.remove('isVisible')
        }
      }
    }, [shadowRef, show])

    // Toggle show/hide
    const toggleShow = useCallback(() => {
      setShow((prev) => !prev)
    }, [])

    // Input range change handler with validation
    const handleRangeChange = useCallback(
      (newValue) => {
        if (newValue.min >= 0.0 && newValue.max <= price) {
          handleChange(newValue)
        }
      },
      [handleChange, price]
    )

    // Generate display value for the field
    const displayValue =
      value.max !== price || value.min !== 0
        ? `${value.min} K - ${value.max} K`
        : translation

    // Check if price range is active
    const isPriceActive = value.max !== price || value.min !== 0

    // Track if price range content should be in DOM with a separate state
    const [rangeMounted, setRangeMounted] = useState(false)

    // Use effect to handle mounting/unmounting with proper timing for transitions
    useEffect(() => {
      if (show) {
        // When opening, mount content immediately
        setRangeMounted(true)
      } else if (!show && rangeMounted) {
        // When closing, wait for transition to finish before unmounting
        const transitionDuration = 300 // Match this with your CSS transition duration
        const timer = setTimeout(() => {
          setRangeMounted(false)
        }, transitionDuration)

        return () => clearTimeout(timer)
      }
    }, [show, rangeMounted])

    return (
      <div ref={wrapperRef} className={styles.field}>
        <CustomField
          type="text"
          aria-label="Select Price Range"
          readOnly
          angle
          variant={[isTablet && 'full-width']}
          onClick={toggleShow}
          isActive={isPriceActive}
          value={displayValue}
          {...props}
        />

        <div
          className={classNames(styles.field__radios, show && styles.isShown)}
        >
          {rangeMounted && (
            <div className="range">
              <InputRange
                maxValue={price}
                minValue={0.0}
                value={value}
                formatLabel={() => null}
                step={0.1}
                onChange={handleRangeChange}
              />

              <div className="range__labels">
                <div>{`${value.min} K`}</div>
                <div>{`${value.max} K`}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)

// Display name for debugging
Price.displayName = 'Price'

export default Price
